import { useState, useEffect } from "react";
import { useParams, Link } from "react-router";
import { getPost, likePost, unlikePost } from "../../api/post.api";
import { ImageCarousel } from "../../components/post/ImageCarousel";
import { CommentSection } from "../../components/post/CommentSection";
import { Skeleton, SkeletonText, SkeletonCircle } from "../../components/ui/Skeleton";
import { useAuthStore } from "../../stores/auth.store";
import type { Post, PostImage } from "@abejon/shared";

interface PostDetail {
  post: Post;
  images: PostImage[];
  business: {
    displayName: string;
    slug: string;
    avatarUrl: string | null;
  } | null;
  isLiked: boolean;
}

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function HeartIcon({ filled }: { filled: boolean }) {
  if (filled) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6">
        <path d="m11.645 20.91-.007-.003-.022-.012a15.247 15.247 0 0 1-.383-.218 25.18 25.18 0 0 1-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0 1 12 5.052 5.5 5.5 0 0 1 16.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 0 1-4.244 3.17 15.247 15.247 0 0 1-.383.219l-.022.012-.007.004-.003.001a.752.752 0 0 1-.704 0l-.003-.001Z" />
      </svg>
    );
  }
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-6 w-6">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z" />
    </svg>
  );
}

export function PostDetailPage() {
  const { id } = useParams<{ id: string }>();
  const user = useAuthStore((s) => s.user);
  const [data, setData] = useState<PostDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [loadKey, setLoadKey] = useState(0);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [commentCount, setCommentCount] = useState(0);
  const [likeLoading, setLikeLoading] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError("");

    async function load() {
      try {
        const result = await getPost(id!);
        if (!cancelled) {
          setData(result);
          setLiked(result.isLiked);
          setLikeCount(result.post.likeCount);
          setCommentCount(result.post.commentCount);
        }
      } catch {
        if (!cancelled) setError("Post not found");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [id, loadKey]);

  async function handleLikeToggle() {
    if (!user || likeLoading) return;
    setLikeLoading(true);

    // Optimistic update
    const wasLiked = liked;
    setLiked(!wasLiked);
    setLikeCount((c) => wasLiked ? c - 1 : c + 1);

    try {
      if (wasLiked) {
        await unlikePost(id!);
      } else {
        await likePost(id!);
      }
    } catch {
      // Revert on error
      setLiked(wasLiked);
      setLikeCount((c) => wasLiked ? c + 1 : c - 1);
    } finally {
      setLikeLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-lg">
        {/* Business header */}
        <div className="flex items-center gap-3 px-4 py-3">
          <SkeletonCircle className="h-9 w-9" />
          <div className="space-y-1.5">
            <SkeletonText className="h-4 w-28" />
            <SkeletonText className="h-3 w-16" />
          </div>
        </div>
        {/* Image */}
        <Skeleton className="aspect-square w-full" />
        {/* Text lines */}
        <div className="space-y-2 px-4 py-3">
          <SkeletonText className="h-4 w-20" />
          <SkeletonText className="h-4 w-full" />
          <SkeletonText className="h-4 w-3/4" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="py-16 text-center">
        <h1 className="mb-2 text-xl font-bold text-gray-300">Post not found</h1>
        <p className="mb-4 text-sm text-gray-500">Failed to load this post.</p>
        <button
          onClick={() => setLoadKey((k) => k + 1)}
          className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
        >
          Retry
        </button>
      </div>
    );
  }

  const { post, images, business } = data;

  return (
    <div className="mx-auto max-w-lg">
      {/* Business header */}
      {business && (
        <div className="flex items-center gap-3 px-4 py-3">
          <Link
            to={`/b/${business.slug}`}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-200 text-sm font-medium text-gray-700"
          >
            {business.avatarUrl ? (
              <img
                src={business.avatarUrl}
                alt=""
                className="h-full w-full rounded-full object-cover"
              />
            ) : (
              business.displayName.charAt(0).toUpperCase()
            )}
          </Link>
          <div>
            <Link
              to={`/b/${business.slug}`}
              className="text-sm font-semibold text-gray-900 hover:underline"
            >
              {business.displayName}
            </Link>
            <p className="text-xs text-gray-400">{timeAgo(post.createdAt)}</p>
          </div>
        </div>
      )}

      {/* Images — full-bleed on mobile */}
      <div className="-mx-4 sm:mx-0">
        <ImageCarousel images={images} />
      </div>

      {/* Actions + Engagement */}
      <div className="px-4 py-3">
        {/* Like button */}
        <div className="flex items-center gap-4">
          <button
            onClick={handleLikeToggle}
            disabled={!user}
            className={`transition-colors ${
              liked ? "text-red-500" : "text-gray-700 hover:text-gray-900"
            } disabled:cursor-default disabled:text-gray-300`}
            aria-label={liked ? "Unlike" : "Like"}
          >
            <HeartIcon filled={liked} />
          </button>
        </div>

        {/* Counts */}
        <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
          <span className="font-semibold text-gray-900">
            {likeCount} {likeCount === 1 ? "like" : "likes"}
          </span>
        </div>

        {/* Caption */}
        {post.caption && (
          <p className="mt-2 text-sm text-gray-900">
            {business && (
              <Link
                to={`/b/${business.slug}`}
                className="mr-1 font-semibold hover:underline"
              >
                {business.displayName}
              </Link>
            )}
            {post.caption}
          </p>
        )}
      </div>

      {/* Comments */}
      <CommentSection
        postId={post.id}
        commentCount={commentCount}
        onCountChange={(delta) => setCommentCount((c) => Math.max(0, c + delta))}
      />
    </div>
  );
}
